import { useState } from "react";

import { Icon, PopoverInteractionKind, Position } from "@blueprintjs/core";
import { Link, useLocation } from "react-router";

import { Popover } from "@/components/helpers/ui/blueprint-overrides/Popover";
import { Media } from "@/components/layout/Media";

import MenuItem from "./PublicNav/MenuItem";
import { LinkType } from "./PublicNav/types";

interface SubMenuViewListProps {
  label: string;
  subMenus: LinkType[];
  onSubMenusVisible?: () => void;
  path?: string;
}

export default function SubMenuViewList({
  label,
  subMenus,
  onSubMenusVisible,
  path,
}: SubMenuViewListProps) {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const isActivePage =
    subMenus.filter((m) => location.pathname.startsWith(m.path)).length > 0;

  const subMenuView = subMenus.map((subMenu, index) => {
    const { title, description, path, newWindow, href, hidden } = subMenu;
    const targetProp = newWindow ? "_blank" : null;
    const relProp = newWindow ? "noopener noreferrer" : undefined;
    const isActiveChildPage = location.pathname.startsWith(path);

    if (hidden || !(path || href)) {
      return null;
    }

    if (path) {
      return (
        <Link
          to={path}
          target={targetProp}
          rel={relProp}
          className={`mt-4 block rounded-md border bg-white px-4 py-2.5 hover:border-blue-600 hover:bg-[#f8fafe] hover:text-blue-600 focus:no-underline ${
            isActiveChildPage
              ? "border-blue-600 text-blue-600"
              : "border-gray-300/50 text-gray-700"
          }`}
          key={index}
        >
          <div className="font-semibold">{title}</div>
          <div className="mt-1 text-sm text-gray-500">{description}</div>
        </Link>
      );
    }

    if (href) {
      return (
        <a
          href={href}
          target={targetProp}
          rel={relProp}
          className={`mt-4 block rounded-md border bg-white px-4 py-2.5 hover:border-blue-600 hover:bg-blue-50 hover:text-blue-600 focus:no-underline ${
            isActiveChildPage
              ? "border-blue-600 text-blue-600"
              : "border-gray-300/50 text-gray-700"
          }`}
          key={index}
        >
          <div className="font-semibold">{title}</div>
          <div className="mt-1 text-sm text-gray-500">{description}</div>
        </a>
      );
    }
    return null;
  });

  const popoverContent = (
    <div className="w-96 rounded bg-slate-50 p-4">{subMenuView}</div>
  );

  return (
    <li>
      <Media lessThan="lg">
        <hr />
        <ul>
          {subMenus.map((subMenu, index) => {
            const { title, path, href } = subMenu;
            if (path || href) {
              return <MenuItem key={index} label={title} path={path || href} />;
            }
            return null;
          })}
        </ul>
        <hr />
      </Media>
      <Media greaterThan="md">
        <a
          className="inline-block px-4 py-2.5 font-medium select-none"
          onMouseEnter={() => {
            if (!menuOpen) {
              setMenuOpen(true);
              if (onSubMenusVisible) {
                onSubMenusVisible();
              }
            }
          }}
          onMouseLeave={() => {
            if (menuOpen) {
              setMenuOpen(false);
            }
          }}
        >
          <Popover
            content={popoverContent}
            interactionKind={PopoverInteractionKind.CLICK}
            isOpen={menuOpen}
            position={Position.BOTTOM_LEFT}
            minimal={true}
            modifiers={{
              arrow: { enabled: false },
              flip: { enabled: false },
            }}
          >
            <div>
              {path ? (
                <Link
                  to={path}
                  className={
                    isActivePage
                      ? "relative text-blue-700 after:absolute after:bottom-[-20px] after:left-0 after:h-[3px] after:w-full after:bg-blue-500 after:content-['']"
                      : "text-gray-700"
                  }
                >
                  {label} <Icon icon="chevron-down" />
                </Link>
              ) : (
                <div
                  className={`flex items-center ${
                    isActivePage
                      ? "relative text-blue-700 after:absolute after:bottom-[-20px] after:left-0 after:h-[3px] after:w-full after:bg-blue-500 after:content-['']"
                      : "text-gray-700"
                  }`}
                >
                  <span>{label}</span>
                  <Icon
                    icon="chevron-down"
                    className="ml-1"
                    color="currentColor"
                  />
                </div>
              )}
            </div>
          </Popover>
        </a>
      </Media>
    </li>
  );
}
